import { UPLOAD_STATUS, type UploadStatus } from '@/constants/upload-workflow';
import type { UploadSession } from '@/context/upload-state';

/** Priority order for upload status display (highest priority first). */
const STATUS_PRIORITY = [
  UPLOAD_STATUS.failed,
  UPLOAD_STATUS.completed,
  UPLOAD_STATUS.paused,
  UPLOAD_STATUS.uploading,
] as const satisfies ReadonlyArray<UploadStatus>;

type HeaderBucketStatus = (typeof STATUS_PRIORITY)[number];

type StatusCount = {
  count: number;
  status: HeaderBucketStatus;
};

/**
 * Calculates status counts from upload sessions for display in toast title.
 * Returns only statuses with at least one upload, in priority order.
 *
 * @param sessions - Array of upload sessions to count statuses from.
 * @returns Array of status counts in priority order, filtered to non-zero counts.
 *
 * @example
 *  With 1 failed and 2 uploading sessions:
 *  Returns [{ status: 'failed', count: 1 }, { status: 'uploading', count: 2 }]
 */
/**
 * Maps session status to toast header bucket (`pending` rolls into `uploading`).
 *
 * @param sessionStatus - Session-level upload status.
 * @returns The bucket status used for toast header display.
 * @example
 * toHeaderBucketStatus('pending'); // => 'uploading'
 */
export function toHeaderBucketStatus(sessionStatus: UploadStatus): HeaderBucketStatus {
  return sessionStatus === UPLOAD_STATUS.pending ? UPLOAD_STATUS.uploading : sessionStatus;
}

export function getStatusCounts(sessions: Array<UploadSession>): Array<StatusCount> {
  const statusCounts = sessions.reduce<Partial<Record<HeaderBucketStatus, number>>>((acc, session) => {
    const bucket = toHeaderBucketStatus(session.status);
    acc[bucket] = (acc[bucket] ?? 0) + 1;
    return acc;
  }, {});

  return STATUS_PRIORITY.filter(status => (statusCounts[status] ?? 0) > 0).map(status => ({
    count: statusCounts[status]!,
    status,
  }));
}
